import { Box, Chip, MenuItem, Typography } from '@mui/material';
import { FileUpload as FileUploadIcon } from '@mui/icons-material';

import {
  CustomAutoComplete,
  CustomButton,
} from '../../../../common/components';
import {
  COST_AREA_OPTIONS,
  COST_BEHAVIOR_OPTIONS,
  COST_TRACEABILITY_OPTIONS,
  getOptionLabel,
} from '../../constants/supplierLedgerConstants';

export const FieldSelect = ({ label, name, value, onChange, options, sx }) => (
  <CustomAutoComplete label={label} name={name} value={value} onChange={onChange} sx={sx || { width: '100%' }}>
    {options.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ))}
  </CustomAutoComplete>
);

export const invoiceFieldSpan = (md, sm = 1) => ({
  gridColumn: {
    xs: 'span 1',
    sm: `span ${sm}`,
    md: `span ${md}`,
  },
});

export const invoiceCompactGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(12, minmax(0, 1fr))',
  },
  columnGap: 1.25,
  rowGap: 1.25,
  alignItems: 'start',
};

export const InvoiceSummaryItem = ({ label, value, highlight = false }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 700, lineHeight: 1.4 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: highlight ? 'text.primary' : 'text.secondary',
        fontWeight: highlight ? 700 : 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
      title={String(value || '-')}
    >
      {value || '-'}
    </Typography>
  </Box>
);

export const summaryControlSx = {
  height: 64,
  minHeight: 64,
};

export const summaryInputSx = {
  mt: 0,
  '& .MuiOutlinedInput-root': {
    height: 64,
  },
  '& .MuiInputBase-input': {
    height: 64,
  },
};

export const AmountPanel = ({ label, value, sx = {} }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'primary.main',
      borderRadius: 1,
      px: 1.5,
      py: 0.75,
      minWidth: 0,
      minHeight: 64,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      backgroundColor: 'background.paper',
      ...sx,
    }}
  >
    <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 700, lineHeight: 1.2 }}>
      {label}
    </Typography>
    <Typography
      variant="h6"
      sx={{
        color: 'primary.main',
        fontWeight: 800,
        lineHeight: 1.15,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
      title={String(value || '-')}
    >
      {value || '-'}
    </Typography>
  </Box>
);

export const AttachmentPicker = ({ file, onFileChange, currentFileName = '', sx = {} }) => (
  <Box
    sx={{
      mt: '12px',
      minWidth: 0,
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'auto minmax(0, 1fr)' },
      alignItems: 'center',
      gap: 1.25,
      height: 56,
      minHeight: 56,
      p: 0.75,
      border: '1px dashed',
      borderColor: 'primary.main',
      borderRadius: 1,
      backgroundColor: 'background.paper',
      ...sx,
    }}
  >
    <input
      id="supplierInvoiceFileUpload"
      type="file"
      accept="application/pdf,image/png,image/jpeg"
      onChange={(event) => onFileChange(event.target.files?.[0] || null)}
      style={{ display: 'none' }}
    />
    <label htmlFor="supplierInvoiceFileUpload" style={{ display: 'flex' }}>
      <CustomButton
        variant="outlined"
        component="span"
        buttonIcon={FileUploadIcon}
        sx={{ height: 40, px: 2, width: { xs: '100%', sm: 'auto' } }}
      >
        Adjuntar
      </CustomButton>
    </label>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 700, lineHeight: 1.2 }}>
        Archivo adjunto
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: file ? 'text.primary' : 'text.secondary',
          fontSize: '0.92rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={file ? file.name : (currentFileName || 'Sin archivo adjunto')}
      >
        {file ? file.name : (currentFileName || 'Sin archivo adjunto')}
      </Typography>
    </Box>
  </Box>
);

const getClassificationLabels = (classification = {}) => [
  getOptionLabel(COST_BEHAVIOR_OPTIONS, classification.cost_behavior),
  getOptionLabel(COST_TRACEABILITY_OPTIONS, classification.cost_nature || classification.cost_traceability),
  getOptionLabel(COST_AREA_OPTIONS, classification.cost_area),
];

// Las facturas traen `classifications` (una por combinacion distinta entre sus importes).
export const ClassificationChips = ({ row }) => {
  const classifications = Array.isArray(row?.classifications) ? row.classifications : [row || {}];
  if (classifications.length > 1) {
    return (
      <Chip
        size="small"
        label="Clasificación mixta"
        title={classifications.map((classification) => getClassificationLabels(classification).join(' / ')).join('\n')}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {getClassificationLabels(classifications[0]).map((label, index) => (
        <Chip key={index} size="small" label={label || 'Sin clasificar'} />
      ))}
    </Box>
  );
};
